import { FabricObject } from 'fabric';
import React, { useImperativeHandle, useRef } from 'react';

import { MediaElement } from 'editor/src/store/design/types';
import { WarningLevel, WarningType } from 'editor/src/store/editorModules/warnings/types';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import { highWarningIcon, lowWarningIcon, mediumWarningIcon } from 'editor/src/util/warningIcon';

import FabricSVGComponent, { SVGElement } from 'editor/src/component/EditorArea/fabricComponents/FabricSVGComponent';
import { ObjectRect } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Image/types';
import useElementWarnings from 'editor/src/component/TopMenuDesktop/buttons/ButtonWarnings/useElementWarnings';

import getWarningIconDimensions from './getWarningIconDimensions';
import useWarningIconViewport from './useWarningIconViewport';

export interface WarningIconInterface {
  set: (options: Partial<FabricObject>) => void;
}

interface Props {
  element: MediaElement;
  frameRect: ObjectRect;
  zIndex: number;
  /**
   * Forces the highest-severity icon regardless of the design-warnings
   * framework's own list — the regex forced-format mismatch is a full-editor
   * canvas concern (see getValueFormatMismatches), not one of the exported
   * warnings that framework tracks, so it cannot add itself to `warnings`
   * the way the others do.
   */
  forceHighWarning?: boolean;
  /** GAPI-61892: the text draws its own overflow marker, so the icon leaves TextOverflow to it. */
  overflowMarkerVisible?: boolean;
}

const WarningIcon = React.forwardRef(
  (
    { element, frameRect, zIndex, forceHighWarning, overflowMarkerVisible }: Props,
    ref: React.Ref<WarningIconInterface>,
  ) => {
    const fabricCanvas = useFabricCanvas();
    const { warnings: elementWarnings } = useElementWarnings(element);
    // GAPI-61892: an overflowing text frame already carries its own red marker on the
    // same corner (drawn by FabricPathText), so the generic icon would only cover it.
    // When an overlay hides that text, the icon is the only cue left and keeps it.
    const warnings = overflowMarkerVisible
      ? elementWarnings.filter(({ type }) => type !== WarningType.TextOverflow)
      : elementWarnings;
    let icon = lowWarningIcon;
    if (warnings.find(({ level }) => level === WarningLevel.Medium)) {
      icon = mediumWarningIcon;
    }
    if (warnings.find(({ level }) => level === WarningLevel.High) || forceHighWarning) {
      icon = highWarningIcon;
    }

    const warningRef = useRef<SVGElement | null>(null);
    useImperativeHandle(
      ref,
      () => ({
        set: (options) => warningRef.current?.set(options),
      }),
      [warningRef],
    );

    useWarningIconViewport(frameRect, warningRef);

    if (!warnings.length && !forceHighWarning) {
      return null;
    }

    return (
      <FabricSVGComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getWarningIconDimensions(frameRect, fabricCanvas.getZoom())}
        ref={warningRef}
        svg={icon}
        evented={false}
        selectable={false}
        zIndex={zIndex}
      />
    );
  },
);

export default React.memo(WarningIcon);
